import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/material";
import {
  useRefreshTimer,
  useSpecificLocationData,
  useLocationContentData,
} from "../../hooks";
import {
  BilletGroup,
  SelectedLocationForMove,
  Location,
  SpecialLocation,
} from "../../models";
import {
  SetCountModal,
  ClearConfirmationModal,
  LocationSection,
  PositionColumn,
  BilletDetailsForm,
} from "./edit-location-modal/components";
import {
  SectionColumn,
  GradeColumn,
  ActionButtonsSection,
  QualityDestinationColumn,
} from "./shared/components";
import {
  DESTINATION_OPTIONS,
  DEFAULT_BILLETS_COUNT,
} from "./edit-location-modal/constants";
import {
  ManualEntryData,
  validateAddBilletForm,
  hasValidEntryData,
  convertSectionToDimensionsString,
  determineIsForSale,
} from "../../utils";
import {
  createBilletsGroup,
  addBillet,
  setBilletsCount,
  clearLocation,
} from "../../repository/BilletYardInventoryRepository";
import { API_CONSTANTS } from "./edit-location-modal/constants/locationContentEdition";

const destinationOptions = DESTINATION_OPTIONS;

// Utility function to get location display text (similar to MoveBilletsModal)
const getLocationDisplayText = (location: Location | SpecialLocation) => {
  return location.HeaderText;
};

// Updated SelectionData interface to include destination
export interface SelectionData {
  gradeValue?: string;
  sectionValue?: string;
  positionId?: number;
  qualityStatusValue?: string;
  destinationValue?: string | null;
}

interface EditLocationModalProps {
  show: boolean;
  onHide: () => void;
  locationId?: number | null;
  selectedCraneId: number;
  refreshInterval?: number;
  canClearLocation: boolean;
}

export const EditLocationModal = ({
  show,
  onHide,
  locationId,
  selectedCraneId,
  refreshInterval = 5,
  canClearLocation,
}: EditLocationModalProps) => {
  // -- hooks
  const { grades, sections, qualityStatuses } = useLocationContentData();

  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedQualities, setSelectedQualities] = useState<string[]>([]);
  const [selectedDestinations, setSelectedDestinations] = useState<
    (string | null)[]
  >([]);
  const [entryData, setEntryData] = useState<ManualEntryData>({
    heatNumber: "",
    length: "",
    billetsCount: DEFAULT_BILLETS_COUNT,
  });

  const [selectionData, setSelectionData] = useState<SelectionData>({});
  const [selectedContentId, setSelectedContentId] = useState<number | null>(
    null,
  );
  const [isOperating, setIsOperating] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showSetCountModal, setShowSetCountModal] = useState(false);
  const [entryErrors, setEntryErrors] = useState<
    Partial<Record<keyof ManualEntryData, string>>
  >({});
  // -----
  // Hooks
  // -----
  const {
    location: currentLocation,
    groups: currentGroups,
    isLoading,
    hasError,
    errorMessage,
    refreshLocationData,
  } = useSpecificLocationData({ locationId });

  const { getSeconds, isRefreshing: isTimerRefreshing } = useRefreshTimer(
    refreshInterval,
    refreshLocationData,
  );

  // Set default selections when modal opens - only Unknown destination (others must be user-selected)
  useEffect(() => {
    if (show) {
      // Set Unknown as default for destinations only (null value) - this is allowed
      setSelectedDestinations([null]);

      // Don't auto-select sections, grades, or qualities since Unknown values can't be used for adding billets
      // Users must explicitly select valid values

      // Update selectionData to reflect the defaults
      setSelectionData((prev) => ({
        ...prev,
        destinationValue: null,
        // Clear other values so users must select them
        sectionValue: undefined,
        gradeValue: undefined,
        qualityStatusValue: undefined,
        positionId: undefined,
      }));
    }
  }, [show]);

  // ----------------------------------
  // User selections & form data entry
  // ---------------------------------
  const handleEntryChange = (field: keyof ManualEntryData, value: string) => {
    setEntryData((prev) => ({ ...prev, [field]: value }));
    if (entryErrors[field]) {
      setEntryErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setOperationError(null);
  };

  const handleSelectionChange = (
    field: keyof SelectionData,
    value: string | number | null,
  ) => {
    setSelectionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (value: string) => {
    const newSections = selectedSections.includes(value)
      ? selectedSections.filter((s) => s !== value)
      : [value]; // Single selection
    setSelectedSections(newSections);
    setSelectionData((prev) => ({
      ...prev,
      sectionValue: newSections[0] || undefined,
    }));
  };

  const handleGradeChange = (value: string) => {
    const newGrades = selectedGrades.includes(value)
      ? selectedGrades.filter((g) => g !== value)
      : [value]; // Single selection
    setSelectedGrades(newGrades);
    setSelectionData((prev) => ({
      ...prev,
      gradeValue: newGrades[0] || undefined,
    }));
  };

  const handleQualityChange = (value: string) => {
    const newQualities = selectedQualities.includes(value)
      ? selectedQualities.filter((q) => q !== value)
      : [value]; // Single selection
    setSelectedQualities(newQualities);
    setSelectionData((prev) => ({
      ...prev,
      qualityStatusValue: newQualities[0] || undefined,
    }));
  };

  const handleDestinationChange = (value: string | null) => {
    const newDestinations = selectedDestinations.includes(value)
      ? selectedDestinations.filter((d) => d !== value)
      : [value]; // Single selection
    setSelectedDestinations(newDestinations);
    setSelectionData((prev) => ({
      ...prev,
      destinationValue: newDestinations[0] || undefined,
    }));
  };

  const handleGroupSelect = (group: BilletGroup) => {
    if (selectedContentId === group.LocationContentID) {
      setSelectedContentId(null);
    } else {
      setSelectedContentId(group.LocationContentID);
    }
  };

  // Handle group selection by global index (for LocationSection component)
  const handleGroupSelectByIndex = (globalIndex: number) => {
    if (
      currentGroups &&
      globalIndex >= 0 &&
      globalIndex < currentGroups.length
    ) {
      const group = currentGroups[globalIndex];
      handleGroupSelect(group);
    }
  };

  // Prepare location data for LocationSection component
  const prepareLocationData = (): SelectedLocationForMove | null => {
    if (!currentLocation || !currentGroups || currentGroups.length === 0) {
      return null;
    }

    // Find the selected group index
    const selectedIndex = currentGroups.findIndex(
      (g) => g.LocationContentID === selectedContentId,
    );

    return {
      location: currentLocation,
      groups: currentGroups,
      selectedGroupIndex: selectedIndex >= 0 ? selectedIndex : null,
      selectedBilletsCount: 0, // Not used in edit location modal
      role: null, // Not used in edit location modal (no source/destination concept)
    };
  };

  const hasValidEntryDataLocal = useMemo(() => {
    // Check if all required fields are properly selected and entry data is valid
    const hasValidSelections =
      selectedSections.length > 0 && // Section selected (Unknown not available)
      selectedGrades.length > 0 &&
      Boolean(selectedGrades[0]) &&
      selectedGrades[0].trim() !== "" && // Valid grade
      selectedQualities.length > 0 &&
      Boolean(selectedQualities[0]) &&
      selectedQualities[0].trim() !== "" && // Valid quality
      selectedDestinations.length > 0 && // Destination can be null (Unknown)
      selectionData.positionId !== undefined &&
      selectionData.positionId !== null; // Position selected

    const hasValidEntry = hasValidEntryData(entryData);

    return Boolean(hasValidSelections && hasValidEntry);
  }, [
    entryData,
    selectedSections,
    selectedGrades,
    selectedQualities,
    selectedDestinations,
    selectionData.positionId,
  ]);

  // ------------------
  // Execution Call
  // ------------------

  // -- Add billets
  const handleAdd = async () => {
    // Early validation
    if (!locationId || isOperating) return;

    try {
      // Set loading state
      setIsOperating(true);
      setOperationError("");

      // Calculate parameters directly
      const { positionId } = selectionData;
      const { heatNumber, length, billetsCount } = entryData;

      // Inline validation
      const validationError = validateAddBilletForm(
        entryData,
        selectedSections,
        selectedGrades,
        selectedQualities,
        selectedDestinations,
        positionId,
      );

      if (validationError) {
        setOperationError(
          validationError + (validationError.endsWith(".") ? "" : "."),
        );
        return;
      }

      // Get values from selection arrays
      const sectionValue = selectedSections[0];
      const gradeValue = selectedGrades[0];
      const qualityStatusValue = selectedQualities[0];
      const destinationValue = selectedDestinations[0];

      // Process parameters
      const dimensionsString = convertSectionToDimensionsString(sectionValue);
      const isForSale = determineIsForSale(destinationValue);
      const gradeIdForAPI = parseInt(gradeValue as string);

      if (isNaN(gradeIdForAPI) || gradeIdForAPI <= 0) {
        setOperationError(
          "Invalid grade selection. Please select a valid grade.",
        );
        return;
      }

      // Call repository directly - Step 1: Create billets group
      const createGroupResponse = await createBilletsGroup(
        heatNumber,
        dimensionsString,
        qualityStatusValue as string,
        parseFloat(length),
        isForSale,
        gradeIdForAPI,
        API_CONSTANTS.CHECK_EXISTENCE,
      );

      // Handle createGroup response
      if (!createGroupResponse.ok) {
        setOperationError(
          createGroupResponse.message || "Failed to create billets group",
        );
        return;
      }

      // Check for warning/error messages in the response
      const tables = createGroupResponse.data?.tables;
      if (
        tables &&
        tables.length > 0 &&
        tables[0].rows &&
        tables[0].rows.length > 0
      ) {
        const firstRow = tables[0].rows[0];
        const statusCode = firstRow.StatusCode;
        const message = firstRow.Message;
        const createdGroupId = firstRow.Data;

        if (statusCode === "F" || statusCode === "R") {
          setOperationError(message || "Failed to create billets group");
          return;
        }

        if (!createdGroupId) {
          setOperationError("Failed to get created group ID from response");
          return;
        }

        // Call repository directly - Step 2: Add billet to location
        const addBilletResponse = await addBillet(
          null, // LocationContentID - null for new billets (matches old system)
          locationId,
          positionId!,
          parseInt(billetsCount),
          parseInt(createdGroupId.toString()),
          selectedCraneId,
          API_CONSTANTS.CHECK_VALIDATION,
        );

        // Handle addBillet response
        if (!addBilletResponse.ok) {
          setOperationError(
            addBilletResponse.message || "Failed to add billets to location",
          );
          return;
        }

        // Check for warning/error messages in addBillet response
        const addTables = addBilletResponse.data?.tables;
        if (
          addTables &&
          addTables.length > 0 &&
          addTables[0].rows &&
          addTables[0].rows.length > 0
        ) {
          const addFirstRow = addTables[0].rows[0];
          const addStatusCode = addFirstRow.StatusCode;
          const addMessage = addFirstRow.Message;

          if (addStatusCode === "F" || addStatusCode === "R") {
            setOperationError(
              addMessage || "Failed to add billets to location",
            );
            return;
          }
        }

        // Success handling - Reset form but keep destination as Unknown
        setEntryData({
          heatNumber: "",
          length: "",
          billetsCount: DEFAULT_BILLETS_COUNT,
        });
        setSelectionData({ destinationValue: null });
        setSelectedSections([]);
        setSelectedGrades([]);
        setSelectedQualities([]);
        setSelectedDestinations([null]); // Keep destination as Unknown

        // Refresh data
        await refreshLocationData();
        setOperationError("");
      }
    } catch (error: unknown) {
      setOperationError(
        error instanceof Error ? error.message : "Failed to add billets",
      );
    } finally {
      setIsOperating(false);
    }
  };

  // -- Set count
  const handleSetCount = () => {
    if (!selectedContentId) {
      setOperationError("Please select a billet group first.");
      return;
    }

    const selectedGroup = currentGroups.find(
      (g) => g.LocationContentID === selectedContentId,
    );
    if (!selectedGroup) {
      setOperationError("Selected billet group not found.");
      return;
    }

    // Open the Set Count modal
    setShowSetCountModal(true);
  };

  const handleSetCountConfirm = async (newCount: number) => {
    // Early validation
    if (isOperating) return;

    const selectedGroup = currentGroups.find(
      (g) => g.LocationContentID === selectedContentId,
    );
    if (!selectedGroup) {
      setOperationError("Selected billet group not found.");
      return;
    }

    try {
      // Set loading state
      setIsOperating(true);
      setOperationError("");

      // Call repository directly - no intermediate layers!
      const response = await setBilletsCount(
        selectedGroup.LocationContentID!, // LocationContentID (required)
        null, // LocationID - null in modal usage per old system
        null, // Position - null in modal usage per old system
        newCount, // New count from user input
        API_CONSTANTS.CHECK_VALIDATION, // CheckValidation
        API_CONSTANTS.CHECK_PROC_PARAMS, // CheckProcParams
        API_CONSTANTS.CHECK_EXISTENCE, // CheckExistence
        API_CONSTANTS.CHECK_POSITION_EXISTENCE, // CheckPositionExistence
        API_CONSTANTS.LOG_TRANSACTION, // LogTransaction
      );

      // Handle response
      if (response.ok) {
        // Check for warning/error messages in the response
        const tables = response.data?.tables;
        if (
          tables &&
          tables.length > 0 &&
          tables[0].rows &&
          tables[0].rows.length > 0
        ) {
          const firstRow = tables[0].rows[0];
          const statusCode = firstRow.StatusCode;
          const message = firstRow.Message;

          if (statusCode === "F" || statusCode === "R") {
            setOperationError(message || "Operation failed");
            return;
          }
        }

        // Success handling
        setShowSetCountModal(false);
        setSelectedContentId(null);
        setOperationError("");

        // Refresh data
        await refreshLocationData();
      } else {
        setOperationError(response.message || "Set count operation failed");
      }
    } catch (error: unknown) {
      setOperationError(
        error instanceof Error ? error.message : "Failed to set billet count",
      );
    } finally {
      setIsOperating(false);
    }
  };

  // -- Clear Location
  const handleClear = () => {
    if (!currentLocation || !locationId) return;
    setShowClearConfirm(true);
  };

  const handleClearConfirm = async () => {
    // Early validation
    if (!currentLocation || !locationId || isOperating) return;

    try {
      // Set loading state
      setShowClearConfirm(false);
      setIsOperating(true);
      setOperationError("");

      // Call repository directly - no intermediate layers!
      const response = await clearLocation(
        locationId,
        selectedCraneId,
        API_CONSTANTS.CHECK_VALIDATION,
      );

      // Handle response
      if (response.ok) {
        // Check for warning/error messages in the response
        const tables = response.data?.tables;
        if (
          tables &&
          tables.length > 0 &&
          tables[0].rows &&
          tables[0].rows.length > 0
        ) {
          const firstRow = tables[0].rows[0];
          const statusCode = firstRow.StatusCode;
          const message = firstRow.Message;

          if (statusCode === "F" || statusCode === "R") {
            setOperationError(message || "Operation failed");
            return;
          }
        }

        // Success handling
        setOperationError("");

        // Refresh data to show empty location
        await refreshLocationData();
      } else {
        setOperationError(
          response.message || "Clear location operation failed",
        );
      }
    } catch (error: unknown) {
      setOperationError(
        error instanceof Error ? error.message : "Failed to clear location",
      );
    } finally {
      setIsOperating(false);
    }
  };

  // -------------------------
  // Modal show/hide handlers
  // -------------------------

  // Reset form state when modal shows/closes (but keep default Unknown selections)
  useEffect(() => {
    if (show) {
      setEntryData({
        heatNumber: "",
        length: "",
        billetsCount: DEFAULT_BILLETS_COUNT,
      });
      // Reset only the form selections, not the default Unknown ones
      setSelectedGrades([]);
      setSelectedQualities([]);
      setSelectedContentId(null);
      setOperationError(null);
      setEntryErrors({});
    }
  }, [show, locationId]);

  const handleClose = () => {
    if (isOperating) return; // Prevent closing during operations
    onHide();
  };

  // Show error if location data fails to load
  if (hasError) {
    return (
      <Dialog
        open={show}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "none",
            width: "90%",
            height: "90%",
          },
        }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
      >
        <DialogTitle
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.primary.contrastText,
            textAlign: "center",
          }}
        >
          Error
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
            }}
          >
            Failed to load location data: {errorMessage}
          </Alert>
        </DialogContent>
        <DialogActions>
          <ActionButtonsSection onClose={handleClose} />
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen
      disableEscapeKeyDown={isOperating}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.primary.contrastText,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
          }}
        >
          Location Content Edition
          {currentLocation && ` - ${currentLocation.HeaderText}`}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: (theme) => theme.spacing(2),
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              flex: 1,
            }}
          >
            <CircularProgress />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading location data...
            </Typography>
          </Box>
        ) : (
          <Box
            display="flex"
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              border: "none",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              paddingTop: "16px",
            }}
          >
            <SectionColumn
              sections={sections || []}
              selectedSections={selectedSections}
              onSectionChange={handleSectionChange}
              customWidth="14%"
            />

            <GradeColumn
              grades={grades || []}
              selectedGrades={selectedGrades}
              onGradeChange={handleGradeChange}
              customWidth="28%"
            />

            <PositionColumn
              currentGroups={currentGroups}
              currentLocation={currentLocation}
              selectedPositions={
                selectionData.positionId !== undefined
                  ? [selectionData.positionId.toString()]
                  : []
              }
              onPositionChange={(value: string) =>
                handleSelectionChange("positionId", parseInt(value))
              }
              customWidth="18%"
            />

            <QualityDestinationColumn
              qualities={qualityStatuses || []}
              selectedQualities={selectedQualities}
              onQualityChange={handleQualityChange}
              destinations={destinationOptions}
              selectedDestinations={selectedDestinations}
              onDestinationChange={handleDestinationChange}
              customWidth="18%"
              showTitleDivider={false}
            >
              {/* Billet Details Form */}
              <Box sx={{ marginTop: "16px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: (theme) => theme.palette.text.primary,
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: (theme) =>
                      `2px solid ${theme.palette.billetYard.borders.light}`,
                    textAlign: "center",
                  }}
                >
                  Billet Details
                </Typography>
                <BilletDetailsForm
                  entryData={entryData}
                  onEntryChange={handleEntryChange}
                  isOperating={isOperating}
                  standalone={false}
                />
              </Box>
            </QualityDestinationColumn>

            <Box
              sx={{
                width: "22%",
                height: "100%",
                marginLeft: "0.5%",
                marginRight: "0.5%",
                background: (theme) =>
                  theme.palette.billetYard.backgrounds.gradient,
                border: (theme) =>
                  `1px solid ${theme.palette.billetYard.borders.light}`,
                borderRadius: "12px",
                padding: "16px",
                overflowY: "auto",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <LocationSection
                location={prepareLocationData()}
                fallbackLocation={currentLocation}
                getLocationDisplayText={getLocationDisplayText}
                showEmptyState={true}
                onGroupSelect={handleGroupSelectByIndex}
              />
            </Box>
          </Box>
        )}

        {operationError && (
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
            }}
          >
            {operationError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: 0,
        }}
      >
        <ActionButtonsSection
          canClearLocation={canClearLocation}
          location={currentLocation}
          selectedGroupId={selectedContentId}
          hasValidEntryData={hasValidEntryDataLocal}
          disableAddButton={!hasValidEntryDataLocal}
          isLoading={isOperating}
          onAdd={handleAdd}
          onSetCount={handleSetCount}
          onClear={handleClear}
          onClose={handleClose}
          getSeconds={getSeconds}
          isRefreshing={isTimerRefreshing}
        />
      </DialogActions>

      {/* Clear Confirmation Modal */}
      <ClearConfirmationModal
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        locationName={currentLocation?.HeaderText || ""}
        isLoading={isOperating}
      />

      {/* Set Count Modal */}
      {selectedContentId && (
        <SetCountModal
          open={showSetCountModal}
          onClose={() => setShowSetCountModal(false)}
          onConfirm={handleSetCountConfirm}
          heatNumber={
            currentGroups.find((g) => g.LocationContentID === selectedContentId)
              ?.HeatNumber || ""
          }
          position={
            currentGroups.find((g) => g.LocationContentID === selectedContentId)
              ?.Position || 0
          }
          currentCount={
            currentGroups.find((g) => g.LocationContentID === selectedContentId)
              ?.BilletsCount || 0
          }
          isLoading={isOperating}
        />
      )}
    </Dialog>
  );
};
